import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// TUNEAMP_CURATOR_SOURCE_TOGGLE_V5

// TUNEAMP_DYNAMIC_CURATOR_SOURCE_V3
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, Coins, ShieldCheck, MusicNotes } from '@phosphor-icons/react';
import SongPicker from '@/components/SongPicker';
import GenreTaxonomySelects from '@/components/GenreTaxonomySelects';

// TUNEAMP_PROMOTION_SOURCE_V2

// TUNEAMP_PROMOTION_URL_V1

// TUNEAMP_MARKETPLACE_PLATFORM_CHANNELS_V2

const PLATFORM_OPTIONS = [
  { value: 'spotify', label: 'Spotify' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'x', label: 'X' },
  { value: 'threads', label: 'Threads' },
  { value: 'vk', label: 'VK' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'soundcloud', label: 'SoundCloud' },
  { value: 'apple_music', label: 'Apple Music' },
  { value: 'amazon_music', label: 'Amazon Music' },
  { value: 'deezer', label: 'Deezer' },
  { value: 'tidal', label: 'TIDAL' },
  { value: 'beatport', label: 'Beatport' },
  { value: 'yandex_music', label: 'Yandex Music' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'discord', label: 'Discord' },
  { value: 'website', label: 'Website' },
  { value: 'blog', label: 'Blog' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'online_magazine', label: 'Online Magazine' },
  { value: 'radio', label: 'Radio' },
  { value: 'radio_show', label: 'Radio Show' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'other', label: 'Other' },
];

const PLATFORM_LABELS = Object.fromEntries(
  PLATFORM_OPTIONS.map((item) => [item.value, item.label])
);

const CURATOR_TYPES = [
  { value: 'playlist_curator', label: 'Playlist curator' },
  { value: 'youtube_channel', label: 'YouTube channel' },
  { value: 'social_media', label: 'Social media' },
  { value: 'blog_media', label: 'Blog / media' },
  { value: 'radio', label: 'Radio' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'record_label', label: 'Record label' },
  { value: 'ar', label: 'A&R' },
  { value: 'manager', label: 'Manager' },
  { value: 'publisher', label: 'Publisher' },
  { value: 'booker', label: 'Booker' },
  { value: 'sync', label: 'Sync' },
  { value: 'other', label: 'Other' },
];

const CURATOR_TYPE_LABELS = Object.fromEntries(
  CURATOR_TYPES.map((item) => [item.value, item.label])
);

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : '—';
};

const recentMetricLabel = (metric) => {
  if (metric === 'playlistViews_30d') return '30d playlist views';
  if (metric === 'playlistStarts_30d') return '30d playlist starts';
  if (metric === 'median_impressions_last_5_posts') return 'median impressions';
  return 'median views';
};

// TUNEAMP_MARKETPLACE_FILTERS_V2

const MARKETPLACE_SELECTED_SONG_KEY =
  'tuneamplifier.marketplace.selectedSongId';

export default function Marketplace() {
  const [curators, setCurators] = useState([]);
  const [songs, setSongs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [selectedSongId, setSelectedSongId] = useState(() => {
    try {
      return sessionStorage.getItem(MARKETPLACE_SELECTED_SONG_KEY) || '';
    } catch {
      return '';
    }
  });

  const [taxonomy, setTaxonomy] = useState({
    genre: '',
    subgenre: '',
    microgenre: '',
  });

  const [platform, setPlatform] = useState('all');
  const [curatorType, setCuratorType] = useState('all');
  const [minCredits, setMinCredits] = useState('');
  const [maxCredits, setMaxCredits] = useState('');
  const [minListeners, setMinListeners] = useState('');
  const [maxListeners, setMaxListeners] = useState('');

  const [loading, setLoading] = useState(false);

  const [submit, setSubmit] = useState(null);
  const [selectedCuratorIds, setSelectedCuratorIds] = useState(new Set());
  const [batchCurators, setBatchCurators] = useState([]);
  const [resolvedPitchLink, setResolvedPitchLink] = useState(null);
  const [songSort, setSongSort] = useState('title');
  const [resolvingPitchLink, setResolvingPitchLink] = useState(false);
  const [form, setForm] = useState({
    song_id: '',
    promotion_mode: 'imported_audio',
    promotion_url: '',
    message: '',
  });

  useEffect(() => {
    Promise.all([
      api.get('/songs'),
      api.get('/playlists'),
    ])
      .then(([songsRes, playlistsRes]) => {
        const nextSongs = Array.isArray(songsRes.data)
          ? songsRes.data
          : [];
        const nextPlaylists = Array.isArray(playlistsRes.data)
          ? playlistsRes.data
          : [];

        setSongs(nextSongs);
        setPlaylists(nextPlaylists);

        const preferredSongId =
          selectedSongId &&
          nextSongs.some((song) => song.song_id === selectedSongId)
            ? selectedSongId
            : nextSongs[0]?.song_id || '';

        setSelectedSongId(preferredSongId);

        if (preferredSongId) {
          try {
            sessionStorage.setItem(
              MARKETPLACE_SELECTED_SONG_KEY,
              preferredSongId
            );
          } catch {
            // Ignore storage failures.
          }
        }

        setForm((prev) => ({
          ...prev,
          song_id: preferredSongId,
          promotion_mode: 'imported_audio',
          promotion_url: '',
        }));
      })
      .catch(() => {
        setSongs([]);
        setPlaylists([]);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCurators = async () => {
      setLoading(true);

      try {
        const params = new URLSearchParams();

        if (selectedSongId) {
          params.set('song_id', selectedSongId);
        }

        if (taxonomy.genre) {
          params.set('genre', taxonomy.genre);
        }

        if (taxonomy.subgenre) {
          params.set('subgenre', taxonomy.subgenre);
        }

        if (taxonomy.microgenre) {
          params.set('microgenre', taxonomy.microgenre);
        }

        if (platform !== 'all') {
          params.set('platform', platform);
        }

        if (curatorType !== 'all') {
          params.set('curator_type', curatorType);
        }

        if (minCredits !== '') {
          params.set('min_credits', minCredits);
        }

        if (maxCredits !== '') {
          params.set('max_credits', maxCredits);
        }

        if (minListeners !== '') {
          params.set('min_listeners', minListeners);
        }

        if (maxListeners !== '') {
          params.set('max_listeners', maxListeners);
        }

        const endpoint = selectedSongId
          ? `/curators/match?${params.toString()}`
          : `/curators${params.toString() ? `?${params.toString()}` : ''}`;

        const { data } = await api.get(endpoint);

        if (!cancelled) {
          setCurators(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setCurators([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCurators();

    return () => {
      cancelled = true;
    };
  }, [
    selectedSongId,
    taxonomy.genre,
    taxonomy.subgenre,
    taxonomy.microgenre,
    platform,
    curatorType,
    minCredits,
    maxCredits,
    minListeners,
    maxListeners,
  ]);

  const resetFilters = () => {
    setTaxonomy({
      genre: '',
      subgenre: '',
      microgenre: '',
    });
    setPlatform('all');
    setCuratorType('all');
    setMinCredits('');
    setMaxCredits('');
    setMinListeners('');
    setMaxListeners('');
  };

  const selectedSong =
    songs.find((song) => song.song_id === selectedSongId) || null;

  const importedSongs = songs.filter(
    (song) => Boolean(song.audio_url)
  );

  const selectedCuratorDocs = curators.filter((curator) =>
    selectedCuratorIds.has(curator.curator_id)
  );

  const selectedCuratorTotal = selectedCuratorDocs.reduce(
    (sum, curator) => sum + (Number(curator.credits_cost) || 0),
    0
  );

  const toggleCurator = (curatorId) => {
    setSelectedCuratorIds((prev) => {
      const next = new Set(prev);

      if (next.has(curatorId)) {
        next.delete(curatorId);
      } else {
        next.add(curatorId);
      }

      return next;
    });
  };

  const selectAllMatchingCurators = () => {
    setSelectedCuratorIds(
      new Set(curators.map((curator) => curator.curator_id))
    );
  };

  const clearCuratorSelection = () => {
    setSelectedCuratorIds(new Set());
  };

  useEffect(() => {
    setSelectedCuratorIds(new Set());
  }, [
    selectedSongId,
    taxonomy.genre,
    taxonomy.subgenre,
    taxonomy.microgenre,
    platform,
    curatorType,
    minCredits,
    maxCredits,
    minListeners,
    maxListeners,
  ]);


  // TUNEAMP_CURATOR_SOURCE_FLOW_V4_HELPERS

  const getPitchPreviewSrc = (data) => {
    if (!data) return '';

    if (data.soundcloud_embed_html) {
      const match = data.soundcloud_embed_html.match(
        /src=["']([^"']+)["']/i
      );

      if (match?.[1]) {
        return match[1];
      }
    }

    if (data.soundcloud_url) {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(
        data.soundcloud_url
      )}&color=%23ccff00&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false`;
    }

    if (data.spotify_url) {
      const match = data.spotify_url.match(
        /open\.spotify\.com\/(track|album)\/([A-Za-z0-9]+)/
      );

      if (match) {
        return `https://open.spotify.com/embed/${match[1]}/${match[2]}?utm_source=tuneamplifier`;
      }
    }

    if (data.youtube_url) {
      const match = data.youtube_url.match(
        /(?:youtu\.be\/|youtube\.com\/watch\?v=)([A-Za-z0-9_-]+)/
      );

      if (match) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    return '';
  };

  const resolvePitchLink = async (url) => {
    const value = String(url || '').trim();

    if (!value) {
      toast.error('Paste the song link first');
      return null;
    }

    if (!/^https?:\/\//i.test(value)) {
      toast.error('Song link must start with http:// or https://');
      return null;
    }

    setResolvingPitchLink(true);

    try {
      const { data } = await api.post('/links/resolve', {
        url: value,
      });

      const resolved = {
        ...data,
        source_url: value,
        preview_src: getPitchPreviewSrc(data),
      };

      setResolvedPitchLink(resolved);

      return resolved;
    } catch (e) {
      setResolvedPitchLink(null);

      toast.error(
        e.response?.data?.detail ||
          "Couldn't read that song link"
      );

      return null;
    } finally {
      setResolvingPitchLink(false);
    }
  };

  const openPitchDialog = (targets) => {
    const targetList = Array.isArray(targets)
      ? targets.filter(Boolean)
      : [];

    if (!targetList.length) {
      toast.error('Pick at least one curator');
      return;
    }

    const mode = form.promotion_mode;

    if (mode === 'imported_audio') {
      const songId = form.song_id || selectedSongId;

      const pitchSong = songs.find(
        (song) => song.song_id === songId
      );

      if (!pitchSong?.audio_url) {
        toast.error('Select an imported song with audio first');
        return;
      }

      setForm((prev) => ({
        ...prev,
        song_id: songId,
        promotion_mode: 'imported_audio',
        promotion_url: '',
      }));

      setResolvedPitchLink(null);
    } else {
      const url = String(form.promotion_url || '').trim();

      if (!url) {
        toast.error('Paste the song link first');
        return;
      }

      if (!/^https?:\/\//i.test(url)) {
        toast.error(
          'Song link must start with http:// or https://'
        );
        return;
      }

      setForm((prev) => ({
        ...prev,
        song_id: '',
        promotion_mode: 'link',
        promotion_url: url,
      }));

      setResolvedPitchLink(null);
    }

    if (targetList.length === 1) {
      setSubmit(targetList[0]);
      setBatchCurators([]);
    } else {
      setSubmit(null);
      setBatchCurators(targetList);
    }

    if (mode === 'link') {
      const url = String(form.promotion_url || '').trim();

      window.setTimeout(() => {
        resolvePitchLink(url);
      }, 0);
    }
  };

  const send = async () => {
    // TUNEAMP_CURATOR_SUBMISSION_SOURCE_V2

    const mode = form.promotion_mode;
    const promotionUrl = String(form.promotion_url || '').trim();

    // ----------------------------------------------------------
    // 1. Validate selected source
    // ----------------------------------------------------------

    if (mode === 'imported_audio') {
      const song = songs.find(
        (item) => item.song_id === form.song_id
      );

      if (!song) {
        toast.error('Pick an imported song');
        return;
      }

      if (!song.audio_url) {
        toast.error('This song has no imported audio');
        return;
      }
    } else {
      if (!promotionUrl) {
        toast.error('Paste the song link for the curator');
        return;
      }

      if (!/^https?:\/\//i.test(promotionUrl)) {
        toast.error(
          'Song link must start with http:// or https://'
        );
        return;
      }

      // Safety: resolve again on Send if the popup was opened
      // before the resolver finished or the URL changed.
      if (
        !resolvedPitchLink ||
        resolvedPitchLink.source_url !== promotionUrl
      ) {
        const resolved = await resolvePitchLink(promotionUrl);

        if (!resolved) {
          return;
        }
      }
    }

    // For public links there is intentionally no song_id.
    const submissionSongId =
      mode === 'imported_audio'
        ? form.song_id
        : null;

    // ----------------------------------------------------------
    // 2. Batch submission
    // ----------------------------------------------------------

    if (batchCurators.length > 0) {
      try {
        const { data } = await api.post('/submissions/batch', {
          curator_ids: batchCurators.map(
            (curator) => curator.curator_id
          ),
          song_id: submissionSongId,
          promotion_mode: mode,
          promotion_url:
            mode === 'link'
              ? promotionUrl
              : null,
          message: form.message,
        });

        const count = Number(
          data?.count ?? batchCurators.length
        );

        const credits = Number(
          data?.credits_charged ?? selectedCuratorTotal
        );

        toast.success(
          `Submission sent to ${count} curators · ${credits} credits`
        );

        setSubmit(null);
        setBatchCurators([]);
        setSelectedCuratorIds(new Set());
        setResolvedPitchLink(null);
        setResolvingPitchLink(false);

        setForm((prev) => ({
          ...prev,
          song_id: '',
          promotion_mode: 'link',
          promotion_url: '',
          message: '',
        }));
      } catch (e) {
        toast.error(
          e.response?.data?.detail || 'Failed to submit'
        );
      }

      return;
    }

    // ----------------------------------------------------------
    // 3. Single submission
    // ----------------------------------------------------------

    if (!submit?.curator_id) {
      toast.error('Pick a curator');
      return;
    }

    try {
      await api.post('/submissions', {
        curator_id: submit.curator_id,
        song_id: submissionSongId,
        promotion_mode: mode,
        promotion_url:
          mode === 'link'
            ? promotionUrl
            : null,
        message: form.message,
      });

      toast.success(
        `Submission sent to ${submit.name}. Credits charged for playlist consideration.`
      );

      setSubmit(null);
      setResolvedPitchLink(null);
      setResolvingPitchLink(false);

      setForm((prev) => ({
        ...prev,
        song_id: '',
        promotion_mode: 'link',
        promotion_url: '',
        message: '',
      }));
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Failed to submit'
      );
    }
  };
  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8 flex items-end justify-between gap-6">
        <div>
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
            Curator Marketplace
          </div>

          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">
            Find the right curator, instantly.
          </h1>

          <p className="text-zinc-400 mt-1 text-sm max-w-2xl">
            Match your song against available curators using genre, taxonomy,
            platform/channel and curator type.
          </p>
        </div>

        <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-[#ccff00] px-3 py-1.5 border border-[#ccff00]/30 rounded-full">
          <ShieldCheck size={14} weight="fill" />
          Verified audience
        </div>
      </header>

      <div className="space-y-5">
        <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <div className="font-display text-lg">
                Pitch source
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                Choose what the curator should receive when you pitch.
              </div>
            </div>

            <div className="text-[10px] font-mono text-zinc-600 uppercase">
              Required before Pitch
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="marketplace-source-imported"

              onClick={() => {
                  const nextSong =
                    selectedSong ||
                    songs.find(
                      (song) => song.song_id === selectedSongId
                    ) ||
                    songs[0] ||
                    null;

                  const nextSongId = nextSong?.song_id || '';

                  setSelectedSongId(nextSongId);

                  try {
                    if (nextSongId) {
                      sessionStorage.setItem(
                        MARKETPLACE_SELECTED_SONG_KEY,
                        nextSongId
                      );
                    }
                  } catch {
                    // Ignore storage failures.
                  }

                  setForm((prev) => ({
                    ...prev,
                    song_id: nextSongId,
                    promotion_mode: 'imported_audio',
                    promotion_url: '',
                  }));
              }}
              className={`text-left rounded-xl border p-4 transition-colors ${
                form.promotion_mode === 'imported_audio'
                  ? 'border-[#ccff00] bg-[#ccff00]/10'
                  : 'border-zinc-800 bg-zinc-950'
              } 'hover:border-zinc-600'`}
            >
              <div className="text-sm font-mono text-zinc-200">
                Imported songs
              </div>

              <div className="text-[10px] text-zinc-600 mt-1">
                Use an MP3 already imported into TuneAmplifier.
              </div>
            </button>

            <button
              type="button"
              data-testid="marketplace-source-link"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  song_id: '',
                  promotion_mode: 'link',
                }))
              }
              className={`text-left rounded-xl border p-4 transition-colors ${
                form.promotion_mode === 'link'
                  ? 'border-[#ccff00] bg-[#ccff00]/10'
                  : 'border-zinc-800 bg-zinc-950'
              } hover:border-zinc-600`}
            >
              <div className="text-sm font-mono text-zinc-200">
                Use link song
              </div>

              <div className="text-[10px] text-zinc-600 mt-1">
                Send a public Spotify, YouTube, SoundCloud or other music link.
              </div>
            </button>
          </div>

          {form.promotion_mode === 'imported_audio' ? (
            <div className="mt-4">
              <SongPicker
                songs={songs}
                playlists={playlists}
                selectedSongId={selectedSongId}
                onSongChange={(value) => {
                  const nextSongId = value || '';

                  setSelectedSongId(nextSongId);

                  try {
                    if (nextSongId) {
                      sessionStorage.setItem(
                        MARKETPLACE_SELECTED_SONG_KEY,
                        nextSongId
                      );
                    } else {
                      sessionStorage.removeItem(
                        MARKETPLACE_SELECTED_SONG_KEY
                      );
                    }
                  } catch {
                    // Ignore storage failures.
                  }

                  setForm((prev) => ({
                    ...prev,
                    song_id: nextSongId,
                    promotion_mode: 'imported_audio',
                    promotion_url: '',
                  }));
                }}
                playlistId=""
                songSort={songSort}
                onSongSortChange={setSongSort}
                manageSongs
                onSongsChange={(nextSongs) => {
                  setSongs(
                    Array.isArray(nextSongs)
                      ? nextSongs
                      : []
                  );
                }}
              />
            </div>
          ) : (
            <div className="mt-4">
              <label className="font-mono text-[10px] uppercase text-zinc-500">
                Public song link
              </label>

              <Input
                type="url"
                data-testid="marketplace-public-song-link"
                value={form.promotion_url}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    song_id: '',
                    promotion_mode: 'link',
                    promotion_url: e.target.value,
                  }))
                }
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData('text').trim();
                  if (/^https?:\/\//i.test(pasted)) {
                    window.setTimeout(() => resolvePitchLink(pasted), 0);
                  }
                }}
                placeholder="Paste the public song link…"
                className="mt-1 bg-zinc-950 border-zinc-800"
              />

              <div className="mt-1 text-[10px] font-mono text-zinc-600">
                The link is resolved automatically when you open Pitch.
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2 text-[10px] font-mono text-zinc-600">
            <span>
              {selectedSongId
                ? 'FIT matching is active for the selected song.'
                : 'FIT matching is unavailable until a song is selected.'}
            </span>

            <span className="text-zinc-800">·</span>

            <span>
              {loading
                ? 'Matching curators…'
                : `${curators.length} curator${curators.length === 1 ? '' : 's'} found`}
            </span>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <div className="font-display text-lg">
                Filters
              </div>

              <div className="text-xs text-zinc-500 mt-1">
                All selected filter categories must match. Multiple values inside
                one category are treated as alternatives.
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={resetFilters}
              className="border-zinc-800"
            >
              Reset filters
            </Button>
          </div>

          <GenreTaxonomySelects
            genre={taxonomy.genre}
            subgenre={taxonomy.subgenre}
            microgenre={taxonomy.microgenre}
            includeAll
            prefix="marketplace"
            onChange={setTaxonomy}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500">
                Platform / channel
              </label>

              <Select
                value={platform}
                onValueChange={setPlatform}
              >
                <SelectTrigger
                  data-testid="marketplace-platform-select"
                  className="mt-1 bg-zinc-950 border-zinc-800"
                >
                  <SelectValue placeholder="All platforms" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">All platforms</SelectItem>

                  {PLATFORM_OPTIONS.map((item) => (
                    <SelectItem
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500">
                Curator type
              </label>

              <Select
                value={curatorType}
                onValueChange={setCuratorType}
              >
                <SelectTrigger
                  data-testid="marketplace-curator-type-select"
                  className="mt-1 bg-zinc-950 border-zinc-800"
                >
                  <SelectValue placeholder="All curator types" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">
                    All curator types
                  </SelectItem>

                  {CURATOR_TYPES.map((item) => (
                    <SelectItem
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500">
                Min credit price
              </label>

              <Input
                type="number"
                min="0"
                value={minCredits}
                onChange={(e) => setMinCredits(e.target.value)}
                placeholder="Any"
                className="mt-1 bg-zinc-950 border-zinc-800"
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500">
                Max credit price
              </label>

              <Input
                type="number"
                min="0"
                value={maxCredits}
                onChange={(e) => setMaxCredits(e.target.value)}
                placeholder="Any"
                className="mt-1 bg-zinc-950 border-zinc-800"
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500">
                Min verified listeners
              </label>

              <Input
                type="number"
                min="0"
                value={minListeners}
                onChange={(e) => setMinListeners(e.target.value)}
                placeholder="Any"
                className="mt-1 bg-zinc-950 border-zinc-800"
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500">
                Max verified listeners
              </label>

              <Input
                type="number"
                min="0"
                value={maxListeners}
                onChange={(e) => setMaxListeners(e.target.value)}
                placeholder="Any"
                className="mt-1 bg-zinc-950 border-zinc-800"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs font-mono text-zinc-500">
            {curators.length} matching curator
            {curators.length === 1 ? '' : 's'}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearCuratorSelection}
              disabled={selectedCuratorIds.size === 0}
              className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
                selectedCuratorIds.size === 0
                  ? 'border-zinc-900 text-zinc-700 cursor-not-allowed'
                  : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300'
              }`}
            >
              Clear
            </button>

            <button
              type="button"
              data-testid="select-all-curators"
              onClick={selectAllMatchingCurators}
              disabled={curators.length === 0}
              className={`text-xs font-mono px-3 py-2 rounded-full border transition-colors ${
                selectedCuratorIds.size === curators.length &&
                curators.length > 0
                  ? 'border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]'
                  : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
              } ${
                curators.length === 0
                  ? 'opacity-40 cursor-not-allowed'
                  : ''
              }`}
            >
              Select all matching
            </button>
          </div>
        </div>

        {curators.length === 0 && !loading && (
          <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-8 text-center">
            <div className="font-display text-xl">
              No matching curators
            </div>

            <div className="text-sm text-zinc-500 mt-2">
              Change the song or filters to see available curators.
            </div>
          </div>
        )}

        {loading && (
          <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-8 text-center">
            <div className="font-display text-lg">
              Matching curators…
            </div>

            <div className="text-xs text-zinc-600 mt-2 font-mono">
              Calculating FIT and loading verified audience data.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger-in pb-28">
          {curators.map((c) => {
            const isSelected = selectedCuratorIds.has(c.curator_id);

            const platformLabel =
              PLATFORM_LABELS[c.platform] || c.platform || '—';

            const curatorTypeLabel =
              CURATOR_TYPE_LABELS[c.curator_type] ||
              c.curator_type ||
              '—';

            const audience =
              c.followers ??
              c.subscribers ??
              c.verified_reach ??
              null;

            const acceptanceRate =
              c.acceptance_rate !== null &&
              c.acceptance_rate !== undefined
                ? `${(Number(c.acceptance_rate) * 100).toFixed(0)}%`
                : '—';

            const refundRateValue =
              c.refund_rate ??
              c.refund_rate_30d ??
              null;

            const refundRate =
              refundRateValue !== null &&
              refundRateValue !== undefined
                ? `${(Number(refundRateValue) * 100).toFixed(0)}%`
                : '—';

            const fit =
              c.fit_percent !== null &&
              c.fit_percent !== undefined
                ? `${Number(c.fit_percent)}%`
                : '—';

            return (
              <div
                key={c.curator_id}
                data-testid={`curator-${c.curator_id}`}
                className={`border rounded-xl bg-zinc-900/40 p-5 card-hover flex flex-col transition-[border-color,background-color] ${
                  isSelected
                    ? 'border-[#ccff00] bg-[#ccff00]/5'
                    : 'border-zinc-800'
                }`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <button
                    type="button"
                    aria-label={
                      isSelected
                        ? 'Remove curator from selection'
                        : 'Select curator'
                    }
                    onClick={() => toggleCurator(c.curator_id)}
                    className={`shrink-0 w-6 h-6 rounded border flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'border-[#ccff00] bg-[#ccff00] text-black'
                        : 'border-zinc-700 text-transparent hover:border-zinc-500'
                    }`}
                  >
                    ✓
                  </button>

                  <img
                    src={c.avatar}
                    alt=""
                    className="w-12 h-12 rounded-lg object-cover border border-zinc-800"
                  />

                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight truncate">
                      {c.playlist_name || c.name || 'Curator'}
                    </div>

                    <div className="text-xs font-mono text-zinc-500 mt-0.5 truncate">
                      {c.name ? `curated by ${c.name}` : curatorTypeLabel}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="text-[10px] font-mono uppercase px-2 py-1 rounded-full border border-zinc-800 text-zinc-400">
                    {platformLabel}
                  </span>

                  <span className="text-[10px] font-mono uppercase px-2 py-1 rounded-full border border-zinc-800 text-zinc-400">
                    {curatorTypeLabel}
                  </span>

                  <span
                    className={`text-[10px] font-mono uppercase px-2 py-1 rounded-full border ${
                      c.pricing_status === 'verified'
                        ? 'border-[#ccff00]/30 text-[#ccff00]'
                        : 'border-amber-500/30 text-amber-400'
                    }`}
                  >
                    {c.pricing_status === 'verified'
                      ? 'verified'
                      : c.pricing_status === 'demo'
                        ? 'demo curator'
                        : c.pricing_status === 'pending'
                          ? 'pending verification'
                          : 'available'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="border border-zinc-800 rounded-lg bg-zinc-950/50 p-3">
                    <div className="text-[9px] font-mono uppercase text-zinc-600">
                      FIT
                    </div>

                    <div className="text-2xl font-display text-[#ccff00] mt-1">
                      {fit}
                    </div>
                  </div>

                  <div className="border border-zinc-800 rounded-lg bg-zinc-950/50 p-3">
                    <div className="text-[9px] font-mono uppercase text-zinc-600">
                      Cost
                    </div>

                    <div className="flex items-center gap-1 text-2xl font-display text-zinc-100 mt-1">
                      <Coins size={17} weight="fill" />
                      {c.credits_cost ?? '—'}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/30">
                    <div className="text-[9px] font-mono uppercase text-zinc-600">
                      Followers / reach
                    </div>

                    <div className="text-sm font-mono text-zinc-300 mt-1">
                      {formatNumber(audience)}
                    </div>
                  </div>

                  <div className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/30">
                    <div className="text-[9px] font-mono uppercase text-zinc-600">
                      Acceptance
                    </div>

                    <div className="text-sm font-mono text-zinc-300 mt-1">
                      {acceptanceRate}
                    </div>
                  </div>

                  <div className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/30">
                    <div className="text-[9px] font-mono uppercase text-zinc-600">
                      Refund rate
                    </div>

                    <div className="text-sm font-mono text-zinc-300 mt-1">
                      {refundRate}
                    </div>
                  </div>

                  <div className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/30">
                    <div className="text-[9px] font-mono uppercase text-zinc-600">
                      Recent activity
                    </div>

                    <div className="text-sm font-mono text-zinc-300 mt-1">
                      {recentMetricLabel(c.pricing_metric)}
                    </div>
                  </div>
                </div>

                <div className="text-[10px] font-mono text-zinc-500 mb-4">
                  <span className="text-zinc-600">Verified reach:</span>{' '}
                  <span className="text-zinc-300">
                    {formatNumber(c.verified_reach)}
                  </span>
                </div>

                {Array.isArray(c.recent_items) &&
                  c.recent_items.length > 0 && (
                    <div className="mb-4 border border-zinc-800 rounded-lg p-3 bg-zinc-950/40">
                      <div className="text-[9px] font-mono uppercase text-zinc-500 mb-2">
                        Recent
                      </div>

                      <div className="space-y-1.5">
                        {c.recent_items.slice(0, 5).map((item, index) => (
                          <div
                            key={item.id || item.url || index}
                            className="flex items-center justify-between gap-3 text-xs"
                          >
                            <span className="truncate text-zinc-300">
                              {item.title || item.name || 'Untitled'}
                            </span>

                            {item.views !== null &&
                              item.views !== undefined && (
                                <span className="shrink-0 text-zinc-500 font-mono">
                                  {formatNumber(item.views)}
                                </span>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                <div className="mt-auto">
                  <Button
                    data-testid={`pitch-${c.curator_id}`}
                    disabled={!(
                      (form.promotion_mode === 'imported_audio' &&
                        form.song_id &&
                        importedSongs.some(
                          (song) =>
                            song.song_id === form.song_id &&
                            Boolean(song.audio_url)
                        )) ||
                      (form.promotion_mode === 'link' &&
                        form.promotion_url.trim() &&
                        /^https?:\/\//i.test(form.promotion_url.trim()))
                    )}
                    onClick={() => openPitchDialog([c])}
                    className="btn-neon rounded-full font-display w-full"
                  >
                    <MusicNotes
                      size={16}
                      className="mr-2"
                      weight="fill"
                    />
                    Pitch a song
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedCuratorIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 border border-[#ccff00]/50 rounded-2xl bg-zinc-950/95 backdrop-blur-md shadow-2xl px-4 py-3 flex flex-wrap items-center gap-4 max-w-[calc(100vw-2rem)]">
          <div className="flex items-center gap-3">
            <Users size={18} className="text-[#ccff00]" />

            <div>
              <div className="font-display text-lg">
                {selectedCuratorIds.size}
              </div>

              <div className="font-mono text-xs text-zinc-500">
                curator{selectedCuratorIds.size !== 1 ? 's' : ''} selected
              </div>
            </div>
          </div>

          <div className="h-8 w-px bg-zinc-800 hidden sm:block" />

          <div className="text-xs font-mono">
            <div className="text-[#ccff00]">
              {selectedCuratorTotal} Credits
            </div>

            <div className="text-zinc-600">
              Each curator uses their own credit price
            </div>
          </div>

          <Button
            data-testid="pitch-selected-btn"
            disabled={
              !(
                (form.promotion_mode === 'imported_audio' &&
                  form.song_id &&
                  importedSongs.some(
                    (song) =>
                      song.song_id === form.song_id &&
                      Boolean(song.audio_url)
                  )) ||
                (form.promotion_mode === 'link' &&
                  form.promotion_url.trim() &&
                  /^https?:\/\//i.test(form.promotion_url.trim()))
              )
            }
            onClick={() => openPitchDialog(selectedCuratorDocs)}
            className="btn-neon rounded-full font-display px-5"
          >
            <MusicNotes
              size={14}
              className="mr-2"
              weight="fill"
            />
            Pitch selected
          </Button>
        </div>
      )}

      <Dialog
        open={!!submit || batchCurators.length > 0}
        onOpenChange={(v) => {
          if (!v) {
            setSubmit(null);
            setBatchCurators([]);
            setResolvedPitchLink(null);
            setResolvingPitchLink(false);
          }
        }}
      >
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="font-display">
              {batchCurators.length > 0
                ? `Pitch to ${batchCurators.length} curators`
                : `Pitch to ${submit?.playlist_name || submit?.name || 'curator'}`}
            </DialogTitle>
          </DialogHeader>

          <div className="text-xs font-mono text-zinc-500 bg-zinc-900/60 border border-zinc-800 rounded-md p-3">
            {batchCurators.length > 0 ? (
              <>
                <div className="text-zinc-300">
                  {selectedCuratorTotal} credits total for{' '}
                  {batchCurators.length} curators.
                </div>

                <div className="mt-1">
                  Each curator is charged according to their own price.
                </div>
              </>
            ) : (
              <>
                <div className="text-zinc-300">
                  {submit?.credits_cost ?? '—'} credits are charged for playlist consideration.
                </div>
              </>
            )}

            <div className="mt-1">
              The curator can answer YES, MAYBE or NO.
              On NO, the curator may choose to refund your credits or keep the payment.
            </div>
          </div>

          <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <div className="font-mono text-[10px] uppercase text-zinc-500">
                  Selected source
                </div>

                <div className="text-sm text-zinc-200 mt-1">
                  {form.promotion_mode === 'imported_audio'
                    ? 'Imported song'
                    : 'Public link'}
                </div>
              </div>

              {form.promotion_mode === 'imported_audio' ? (
                <span className="text-[10px] font-mono text-[#ccff00] uppercase">
                  MP3
                </span>
              ) : (
                <span className="text-[10px] font-mono text-[#ccff00] uppercase">
                  LINK
                </span>
              )}
            </div>

            {form.promotion_mode === 'imported_audio' ? (
              selectedSong ? (
                <div>
                  <div className="flex items-center gap-3">
                    {selectedSong.cover_url ? (
                      <img
                        src={selectedSong.cover_url}
                        alt=""
                        className="w-14 h-14 rounded-lg object-cover border border-zinc-800"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg border border-zinc-800 bg-zinc-950 flex items-center justify-center text-zinc-700">
                        <MusicNotes size={20} />
                      </div>
                    )}

                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-100 truncate">
                        {selectedSong.title}
                      </div>

                      <div className="text-xs text-zinc-500 mt-1 truncate">
                        {selectedSong.artist}
                      </div>
                    </div>
                  </div>

                  {selectedSong.audio_url ? (
                    <audio
                      className="w-full mt-4"
                      controls
                      preload="metadata"
                      src={selectedSong.audio_url}
                    />
                  ) : (
                    <div className="text-xs text-red-400 mt-3">
                      This imported song has no persistent audio.
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-zinc-500">
                  No imported song selected.
                </div>
              )
            ) : (
              <div>
                <div className="text-xs font-mono text-zinc-300 break-all">
                  {form.promotion_url}
                </div>

                <div className="mt-4">
                  {resolvingPitchLink ? (
                    <div className="border border-zinc-800 rounded-lg bg-zinc-950 p-4 text-center">
                      <div className="text-sm text-zinc-300">
                        Resolving song…
                      </div>

                      <div className="text-[10px] font-mono text-zinc-600 mt-1">
                        Reading metadata and player information.
                      </div>
                    </div>
                  ) : resolvedPitchLink ? (
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        {resolvedPitchLink.cover_url ? (
                          <img
                            src={resolvedPitchLink.cover_url}
                            alt=""
                            className="w-14 h-14 rounded-lg object-cover border border-zinc-800"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg border border-zinc-800 bg-zinc-950 flex items-center justify-center text-zinc-700">
                            <MusicNotes size={20} />
                          </div>
                        )}

                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-zinc-100 truncate">
                            {resolvedPitchLink.title || 'Untitled'}
                          </div>

                          <div className="text-xs text-zinc-500 mt-1 truncate">
                            {resolvedPitchLink.artist || 'Unknown artist'}
                          </div>

                          {(resolvedPitchLink.genre ||
                            resolvedPitchLink.subgenre ||
                            resolvedPitchLink.microgenre) && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {resolvedPitchLink.genre && (
                                <span className="px-2 py-1 rounded border border-zinc-800 bg-zinc-950 text-[10px] font-mono text-zinc-400">
                                  Genre: {resolvedPitchLink.genre}
                                </span>
                              )}
                              {resolvedPitchLink.subgenre && (
                                <span className="px-2 py-1 rounded border border-zinc-800 bg-zinc-950 text-[10px] font-mono text-zinc-400">
                                  Subgenre: {resolvedPitchLink.subgenre}
                                </span>
                              )}
                              {resolvedPitchLink.microgenre && (
                                <span className="px-2 py-1 rounded border border-zinc-800 bg-zinc-950 text-[10px] font-mono text-zinc-400">
                                  Microgenre: {resolvedPitchLink.microgenre}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {resolvedPitchLink.preview_src ? (
                        <div className="overflow-hidden rounded-lg border border-zinc-800 bg-black">
                          <iframe
                            title="Song preview"
                            src={resolvedPitchLink.preview_src}
                            className="w-full h-[152px]"
                            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="border border-zinc-800 rounded-lg bg-zinc-950 p-3 text-xs text-zinc-500">
                          Metadata resolved, but this link does not expose an embeddable player.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border border-zinc-800 rounded-lg bg-zinc-950 p-3 text-xs text-zinc-500">
                      The song link will be resolved automatically.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="pitch-message"
              className="font-mono text-[10px] uppercase text-zinc-500"
            >
              Message
            </label>

            <Textarea
              id="pitch-message"
              data-testid="pitch-message-input"
              value={form.message}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  message: e.target.value,
                }))
              }
              placeholder="Optional message to the curator…"
              rows={4}
              className="bg-zinc-950 border-zinc-800"
            />
          </div>

          <DialogFooter>
            <Button
              data-testid="pitch-send-btn"
              onClick={send}
              disabled={
                resolvingPitchLink ||
                !(
                  (form.promotion_mode === 'imported_audio' &&
                    form.song_id &&
                    Boolean(selectedSong?.audio_url)) ||
                  (form.promotion_mode === 'link' &&
                    form.promotion_url.trim() &&
                    /^https?:\/\//i.test(form.promotion_url.trim()))
                )
              }
              className="btn-neon rounded-full"
            >
              Send pitch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
